import type { PhaseStatus } from '@sr-test-wizard/shared';

export type IconKind = PhaseStatus | 'locked' | 'idle' | 'paused';

interface Props {
  status: IconKind;
  size?: number;
}

/**
 * Visual status indicator used by the phases rail and sub-step list. Pure CSS
 * (no images) so it inherits theme colours and stays sharp at any size.
 *
 * `in_progress` = work is actively running (blue spinner, Jira-style).
 * `paused` = started / partial but idle right now (amber pause glyph).
 */
export function StatusIcon({ status, size = 16 }: Props) {
  const dim = `${size}px`;
  const common = {
    width: dim,
    height: dim,
    borderRadius: '50%',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: `${size * 0.7}px`,
    lineHeight: 1,
    flexShrink: 0,
  } as const;
  if (status === 'completed') {
    return (
      <span
        className="status-icon status-icon-completed"
        style={{ ...common, background: 'var(--good)', color: '#0d1117' }}
        title="Completed"
      >
        {'\u2713'}
      </span>
    );
  }
  if (status === 'in_progress') {
    return (
      <span
        className="status-icon status-icon-progress"
        style={{ ...common, border: '2px solid var(--info)', color: 'var(--info)' }}
        title="In Progress"
      >
        <span className="spinner" />
      </span>
    );
  }
  if (status === 'paused') {
    return (
      <span
        className="status-icon status-icon-paused"
        style={{ ...common, border: '2px solid var(--warn)', color: 'var(--warn)' }}
        title="Started — not running"
      >
        <span className="status-icon-pause-bars" aria-hidden>
          <span />
          <span />
        </span>
      </span>
    );
  }
  if (status === 'failed') {
    return (
      <span
        className="status-icon status-icon-failed"
        style={{ ...common, background: 'var(--bad)', color: '#fff' }}
        title="Failed"
      >
        !
      </span>
    );
  }
  if (status === 'locked') {
    return (
      <span
        className="status-icon status-icon-locked"
        style={{ ...common, border: '1px solid var(--border-strong)', color: 'var(--text-dim)' }}
        title="Locked"
      >
        {'\u{1F512}'}
      </span>
    );
  }
  if (status === 'skipped') {
    return (
      <span
        className="status-icon status-icon-skipped"
        style={{ ...common, border: '1px dashed var(--border-strong)', color: 'var(--text-dim)' }}
        title="Skipped"
      />
    );
  }
  return (
    <span
      className="status-icon status-icon-pending"
      style={{ ...common, border: '1px solid var(--border)', color: 'var(--text-dim)' }}
      title="Pending"
    />
  );
}
